/* ============================================================
   BatchActivityBridge
   Lives inside AppShell (inside ActivityProvider, inside
   ContentFeedProvider). Watches active generation batches and
   maps them into ActivityContext so the topbar pill ("Generating
   X of N") and Batch detail screen share one source of truth.
   ============================================================ */
import { useEffect, useRef } from 'react';
import { useContentFeed } from '../../hooks/useContentFeed.js';
import { useActivity } from './ActivityProvider';

const ACTIVE = new Set(['queued', 'generating', 'reserved']);

function pickActiveBatch(batches) {
  // Pick the freshest active batch (most recent createdAt).
  const active = (batches || []).filter((b) => b && ACTIVE.has(b.status));
  if (!active.length) return null;
  return active
    .slice()
    .sort((a, b) => Date.parse(b.createdAt || 0) - Date.parse(a.createdAt || 0))[0];
}

function summarize(batch, content) {
  const total = Math.max(1, Number(batch.totalItems || 0));
  const items = (content || []).filter((c) => c.generationBatchId === batch.id);
  const ready = Math.max(
    Number(batch.readyCount || 0),
    items.filter((c) => ['ready', 'scheduled', 'publishing', 'published'].includes(c.status)).length
  );
  const failed = Math.max(
    Number(batch.failedCount || 0),
    items.filter((c) => c.status === 'failed').length
  );
  // Items-driven complete check. content_item.status flips to 'ready' as
  // soon as the TEXT draft saves — even while the visual is still cooking
  // (generation_status='composing', visual_status='generating'). If we
  // declared the batch done at that point we'd fire the "Batch ready"
  // toast 3-9 minutes too early for image_post jobs. So we additionally
  // require that no item is still in an active generation_status.
  const itemTerminal = (c) => {
    if (!['ready', 'failed', 'scheduled', 'publishing', 'published'].includes(c.status)) return false;
    // generationStatus is the pipeline marker — 'composing' / 'visualizing'
    // / 'drafting' all mean the worker is still cooking this item, even
    // though the text portion is saved. Only mark terminal when it's null
    // (no active stage) or explicitly ready/failed.
    if (c.generationStatus && !['ready', 'failed'].includes(c.generationStatus)) return false;
    // visualStillCooking is the per-item flag content-feed sets when
    // visual_status='generating' but the content row is ready (text path).
    if (c.visualStillCooking) return false;
    return true;
  };
  const itemsAllDone = items.length > 0 && items.length >= total && items.every(itemTerminal);
  const topic = batch.topic || items[0]?.title || 'Generating content';
  return { total, ready, failed, topic, itemsAllDone };
}

export default function BatchActivityBridge() {
  const { activeBatches, content } = useContentFeed();
  const { activity, setActivity } = useActivity();
  const lastBatchIdRef = useRef(null);
  const lastDoneRef = useRef(null);
  const lastActiveSignatureRef = useRef('');
  const dismissTimerRef = useRef(null);

  useEffect(() => {
    const batch = pickActiveBatch(activeBatches);

    if (batch) {
      const { total, ready, failed, topic, itemsAllDone } = summarize(batch, content);
      lastBatchIdRef.current = batch.id;
      // Don't clobber a manual 'planning' activity (e.g. strategy save in flight).
      if (activity?.kind === 'planning') return;
      // When all items hit a terminal state we treat the batch as complete
      // regardless of cached batch.status. But guard against re-emitting on
      // every poll tick — without this the bridge was firing setActivity()
      // every 4s with a fresh "done" object, causing the toast to flash
      // (disappear / reappear) and look broken. lastDoneRef tracks which
      // batch we've already announced; subsequent ticks for the same id
      // exit early.
      if (itemsAllDone) {
        if (lastDoneRef.current === batch.id) return;
        lastDoneRef.current = batch.id;
        const msg = failed > 0
          ? `Batch finished · ${ready} of ${total} ready`
          : `Batch ready · ${ready} posts in Library`;
        setActivity({ kind: 'done', message: msg, batchId: batch.id });
        // Auto-dismiss the done toast after 6s so it doesn't sit on screen
        // forever — also prevents the "stuck" feeling when the active→done
        // transition is the only thing the user is waiting on.
        if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = window.setTimeout(() => {
          setActivity((prev) => (prev?.kind === 'done' && prev.batchId === batch.id ? null : prev));
          dismissTimerRef.current = null;
        }, 6000);
        return;
      }
      // Step heuristic: while ready < total, the worker is actively
      // producing copy/images. Past that we read the activity as
      // "finalizing" — only reached when the batch row hasn't yet
      // flipped out of an active status but items aren't all done.
      const step = ready < total ? 'Writing copy & composing visuals' : 'Finalizing';
      // Skip re-emit if nothing in the active signature changed — same
      // batchId + same ready/total/failed/step. Without this, every poll
      // refresh would replace activity with a new object (same values,
      // different identity) and force ActivityPill to re-render, which
      // is what flickered the topbar pill.
      const signature = `${batch.id}|${ready}|${total}|${failed}|${step}`;
      if (lastActiveSignatureRef.current === signature) return;
      lastActiveSignatureRef.current = signature;
      setActivity({
        kind: 'active',
        batchId: batch.id,
        current: ready,
        total,
        failed,
        step,
        project: topic,
        eta: null,
      });
      return;
    }

    // No active batch right now. If we just transitioned away from an active
    // batch (last one we tracked) → emit a done toast once.
    const lastId = lastBatchIdRef.current;
    if (lastId && lastDoneRef.current !== lastId) {
      // Verify the previously tracked batch actually finished — find it in feed.
      const finished = (activeBatches || []).find((b) => b.id === lastId && !ACTIVE.has(b.status));
      // If we can't find it (server pruned it) we still want to clear the pill.
      if (finished) {
        const total = Math.max(1, Number(finished.totalItems || 0));
        const ready = Number(finished.readyCount || 0);
        const msg = ready >= total
          ? `Batch ready · ${ready} posts in Library`
          : `Batch finished · ${ready} of ${total} ready`;
        setActivity({ kind: 'done', message: msg, batchId: lastId });
        if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = window.setTimeout(() => {
          setActivity((prev) => (prev?.kind === 'done' && prev.batchId === lastId ? null : prev));
          dismissTimerRef.current = null;
        }, 6000);
      } else if (activity?.kind === 'active' && activity.batchId === lastId) {
        // Pruned mid-flight: just clear.
        setActivity(null);
      }
      lastDoneRef.current = lastId;
      lastBatchIdRef.current = null;
      lastActiveSignatureRef.current = '';
      return;
    }

    // If activity is stuck on 'active' but no batches at all → clear it.
    if (activity?.kind === 'active' && !batch && !lastId) {
      setActivity(null);
      lastActiveSignatureRef.current = '';
    }
  }, [activeBatches, content]);

  // Cleanup pending dismiss timer if the bridge unmounts.
  useEffect(() => () => {
    if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
  }, []);

  return null;
}
